import { useEffect, useRef, useState } from "react";
import { api } from "../../api";
import { ErrorNote, Loading } from "../../components/Feedback";
import { Btn, Card, Field, G_DARK, Input, PasswordInput } from "../../components/Shared";
import { rememberFocus } from "../../focus";
import { AskDialog, CopyField, SuccessNote, SwitchRow } from "./parts";

// Management -> Sign-in: the Keycloak connection and password sign-in.
// GET/PATCH /api/admin/auth-settings, POST /api/admin/auth-settings/oidc/test.
// After data-portal's SignInCard.jsx.
//
// - The client secret is write-only: the server answers `oidc_client_secret_set`, never the
//   value, and the field always starts blank. A blank secret keeps the stored one, so it is
//   sent only when somebody typed one.
// - A Save sends only what differs from what the server last said: `local_login` and
//   `oidc_enabled` in a body are what the server's guard rails run on, so resending them with a
//   display-name edit could be refused for a switch nobody touched.
// - The guard rails are the server's (switching password sign-in off needs a Keycloak session,
//   Keycloak on and a passing live test; Keycloak cannot be switched off while password sign-in
//   is off). This card adds the one moment somebody reads what the switch does before it
//   happens — the confirmation — and shows the server's refusal verbatim.

const URL = "/admin/auth-settings";

const EDITABLE = [
  "oidc_enabled", "oidc_display_name", "oidc_issuer", "oidc_discovery_url", "oidc_client_id",
  "oidc_allowed_domains", "oidc_self_registration", "local_login",
];

const PASSWORD_SENTENCE = {
  on: "Local accounts (the first administrator, any account created with a password) sign in with a password. A Keycloak account never does.",
  off: "No password sign-in at all: the form is hidden and every password session ends on its next request. Keycloak is then the only way in.",
};

export function formFrom(s) {
  return {
    oidc_enabled: !!s.oidc_enabled,
    oidc_display_name: s.oidc_display_name ?? "",
    oidc_issuer: s.oidc_issuer ?? "",
    oidc_discovery_url: s.oidc_discovery_url ?? "",
    oidc_client_id: s.oidc_client_id ?? "",
    oidc_allowed_domains: s.oidc_allowed_domains ?? "",
    oidc_self_registration: !!s.oidc_self_registration,
    local_login: s.local_login !== false,
    // Always blank, whatever the server sent.
    oidc_client_secret: "",
  };
}

export function changesFrom(form, settings) {
  const stored = formFrom(settings);
  const out = {};
  for (const key of EDITABLE) {
    const v = typeof form[key] === "string" ? form[key].trim() : form[key];
    if (v !== stored[key]) out[key] = v;
  }
  if (form.oidc_client_secret) out.oidc_client_secret = form.oidc_client_secret;
  return out;
}

function ImpactNote({ impact }) {
  const noEmail = Number(impact?.no_email) || 0;
  const shared = Number(impact?.shared_email) || 0;
  const total = noEmail + shared;
  return (
    <p style={{ margin: "8px 0 0", fontSize: 13, color: total ? "#92400e" : "#374151" }}>
      {total === 1 ? "1 active local account would lose its way in" : `${total} active local accounts would lose their way in`}
      {`: ${noEmail} with no e-mail, ${shared} sharing an e-mail with another account. `}
      An account with an e-mail of its own is linked on its first Keycloak sign-in with that address.
    </p>
  );
}

function SignInForm({ settings, onSaved }) {
  const [form, setForm] = useState(() => formFrom(settings));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [copied, setCopied] = useState(null);
  const saveRef = useRef(null);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const flip = k => v => setForm(f => ({ ...f, [k]: v }));

  const send = async payload => {
    const restore = rememberFocus();
    setBusy(true);
    setError(null);
    try {
      const result = await api.patch(URL, payload);
      onSaved(result);
      setForm(formFrom(result));
      setNotice("Saved.");
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
      setConfirming(null);
      restore(() => saveRef.current && saveRef.current.querySelector("button"));
    }
  };

  const save = () => {
    setError(null);
    setNotice(null);
    const payload = changesFrom(form, settings);
    if (Object.keys(payload).length === 0) { setNotice("Nothing has changed."); return; }
    if (payload.local_login === false) { setConfirming(payload); return; }
    send(payload);
  };

  // Tests the SAVED values: they are what the server's refusal to enable is measured against.
  const test = async () => {
    const restore = rememberFocus();
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post(`${URL}/oidc/test`);
      setTestResult({ ok: !!r?.ok, detail: r?.detail || "" });
    } catch (e) {
      setTestResult({ ok: false, detail: e.message });
    } finally {
      setTesting(false);
      restore();
    }
  };

  const copy = key => async () => {
    try {
      await navigator.clipboard.writeText(settings[key] || "");
      setCopied({ key, text: "Copied." });
    } catch {
      setCopied({ key, text: "Could not copy — select the address and copy it by hand." });
    }
  };

  return (
    <Card style={{ marginBottom: 20 }}>
      <h2 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700 }}>Keycloak</h2>
      <p style={{ margin: "0 0 20px", fontSize: 13, color: "#4b5563" }}>
        The portal is a confidential client in your Keycloak realm. Keycloak only proves who somebody
        is (by a verified e-mail, then by its user id); permissions are given here, under Users,
        and project memberships by each project's curators.
      </p>
      <ErrorNote error={error} />

      <CopyField label="Callback URL" name="callback URL"
                 hint="Copy this into the Keycloak client's valid redirect URIs."
                 value={settings.callback_url} onCopy={copy("callback_url")}
                 copied={copied?.key === "callback_url" ? copied.text : null} />
      <CopyField label="Post-logout redirect URI" name="post-logout redirect URI"
                 hint="Copy this into the Keycloak client's valid post logout redirect URIs."
                 value={settings.post_logout_redirect_uri} onCopy={copy("post_logout_redirect_uri")}
                 copied={copied?.key === "post_logout_redirect_uri" ? copied.text : null} />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginBottom: 16 }}>
        <Field label="Issuer URL" hint="The realm's address, e.g. https://login.example.org/realms/unio; https except for localhost.">
          <Input value={form.oidc_issuer} onChange={set("oidc_issuer")} spellCheck={false} />
        </Field>
        <Field label="Discovery URL (optional)" hint="Only when the backend reaches Keycloak at another address than browsers do. Blank uses the issuer.">
          <Input value={form.oidc_discovery_url} onChange={set("oidc_discovery_url")} spellCheck={false} />
        </Field>
        <Field label="Client ID">
          <Input value={form.oidc_client_id} onChange={set("oidc_client_id")} spellCheck={false} />
        </Field>
        <Field label="Client secret" hint={settings.oidc_client_secret_set ? "Currently set — leave blank to keep it" : "Not set"}>
          <PasswordInput value={form.oidc_client_secret} onChange={set("oidc_client_secret")}
                         autoComplete="new-password" placeholder={settings.oidc_client_secret_set ? "•••••••• (unchanged)" : ""} />
        </Field>
        <Field label="Button label" hint="Shown on the sign-in page as “Sign in with …”.">
          <Input value={form.oidc_display_name} onChange={set("oidc_display_name")} placeholder="Keycloak" />
        </Field>
        <Field label="Allowed e-mail domains" hint="Comma-separated. Blank admits any domain.">
          <Input value={form.oidc_allowed_domains} onChange={set("oidc_allowed_domains")} placeholder="boku.ac.at" spellCheck={false} />
        </Field>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 20 }}>
        <SwitchRow label="Sign-in through Keycloak" checked={form.oidc_enabled} onChange={flip("oidc_enabled")}
                   hint="Refused while the issuer, client ID or secret is missing or a connection test on them fails." />
        <SwitchRow label="Self-registration" checked={form.oidc_self_registration} onChange={flip("oidc_self_registration")}
                   hint="On: somebody with no portal account gets one on their first sign-in, with no permission and in no project, and sees nothing until a project's curator adds them. Off: they are told to ask an administrator." />
      </div>

      <div style={{ marginBottom: 20, paddingTop: 16, borderTop: "1px solid #f3f4f6" }}>
        <SwitchRow label="Password sign-in" checked={form.local_login} onChange={flip("local_login")}
                   hint={PASSWORD_SENTENCE[form.local_login ? "on" : "off"]} />
        {!form.local_login && settings.local_login && <ImpactNote impact={settings.local_login_impact} />}
      </div>

      <SuccessNote>{notice}</SuccessNote>
      <div ref={saveRef} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <Btn onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</Btn>
        <Btn variant="secondary" onClick={test} disabled={testing}>
          {testing ? "Testing…" : "Test connection"}
        </Btn>
        <span style={{ fontSize: 12, color: "#4b5563" }}>The test uses the saved values.</span>
      </div>
      {testResult && (
        <div role="status" style={{ marginTop: 10, fontSize: 13, color: testResult.ok ? G_DARK : "#b91c1c" }}>
          <span aria-hidden="true">{testResult.ok ? "✓ " : "✗ "}</span>
          {testResult.detail || (testResult.ok ? "The connection works." : "The connection failed.")}
        </div>
      )}

      {confirming && (
        <AskDialog title="Switch password sign-in off?" confirmLabel="Switch password sign-in off" busy={busy}
                   onConfirm={() => send(confirming)} onClose={() => setConfirming(null)}>
          <p style={{ margin: "0 0 8px", fontSize: 14, color: "#374151" }}>{PASSWORD_SENTENCE.off}</p>
          <ImpactNote impact={settings.local_login_impact} />
          <p style={{ margin: "8px 0 0", fontSize: 13, color: "#4b5563" }}>
            If Keycloak then stops answering, the way back is the break-glass command on the server
            (<code>python -m unio.cli local-login on</code>), not this page.
          </p>
        </AskDialog>
      )}
    </Card>
  );
}

export default function SignInCard() {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    api.get(URL)
      .then(s => { if (live) setSettings(s); })
      .catch(e => { if (live) setError(e); });
    return () => { live = false; };
  }, []);

  if (error) return <ErrorNote error={error} />;
  if (!settings) return <Loading />;
  return <SignInForm settings={settings} onSaved={setSettings} />;
}
